import { Crosshair, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RadiusOnlyToggleProps {
  active: boolean;
  onToggle: () => void;
}

/**
 * `<RadiusOnlyToggle>` — single chip controlling whether properties OUTSIDE
 * any visible amenity radius are HIDDEN entirely on `/app/map`. Sister chip
 * to `<HighlightToggle>` (which only restyles, never hides). Both are
 * additive — the user can run with both off, both on, or any combination.
 *
 * Plan 260502-08r:
 * - Active uses Crosshair + bronze-500 accent (deliberate color shift from
 *   HighlightToggle's moss-500 so two adjacent active chips are visually
 *   distinguishable).
 * - Inactive shows the Filter icon + muted bg.
 * - lucide-react does not export CrosshairOff; Filter is the documented
 *   fallback for the inactive state.
 *
 * Accessibility: `role="switch"` + `aria-checked`; aria-label keeps the
 * verbose phrasing for screen readers.
 */
export function RadiusOnlyToggle({ active, onToggle }: RadiusOnlyToggleProps) {
  const Icon = active ? Crosshair : Filter;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label="Show only properties within an amenity radius"
      onClick={onToggle}
      className={cn(
        'inline-flex h-8 px-3 rounded-full border items-center gap-1.5 text-sm font-semibold cursor-pointer',
        'transition-colors',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        active
          ? 'bg-bronze-500/20 border-bronze-500/60 text-foam-50'
          : 'bg-ocean-800/40 border-foam-50/10 text-foam-50/60',
      )}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      In radius only
    </button>
  );
}
